import cx from "classnames";
import { set } from "date-fns";
import PropTypes from "prop-types";
import React, { useMemo } from "react";
import { Col, Form, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import { isEmptyOrNull } from "../../utils";
import { moment } from "../../utils/date";
import { CalenderContainer } from "./IFXDatePicker";

const DEFAULT_PROPS = {
  className: "ifx-date-range-picker",
  dateFormat: "MM/dd/yyyy",
  showMonthDropdown: true,
  showYearDropdown: true,
  useShortMonthInDropdown: true,
  highlightDates: [new Date()],
  size: "sm",
  serializeFormat: "MM/DD/YYYY",
  isSerialized: true,
  autoComplete: "off",
  dropdownMode: "scroll",
  minDate: set(new Date(), {
    year: 1976,
    month: 0,
    date: 1,
    hours: 0,
    minutes: 0,
  }),
  maxDate: set(new Date(), {
    year: 2500,
    month: 12,
    date: 31,
    hours: 23,
    minutes: 59,
  }),
  split: true,
  isClearable: true,
};

export const IFXDateRangePicker = ({
  isInvalid = [],
  required = [],
  fromColProps,
  toColProps,
  minTime,
  maxTime,
  disableForPastDate = false,
  includeTodaysDateForPast = false,
  disablePastDateSelection = false,
  ...props
}) => {
  const {
    onFocus = [],
    onBlur = [],
    onCalendarClose = [],
    controlId = [],
    label = [],
    alwaysFloat = [],
    wrapperState = { from: {}, to: {} },
    selected = {},
    disabled = [],
    customStartDate = undefined,
    customEndDate = undefined,
    copyTo = false,
  } = props;
  //const { rangeErrorComponent = undefined } = props;
  //const { fromError = undefined, toError = undefined } = rangeErrorComponent;
  const _props = { ...DEFAULT_PROPS, className: "", ...props };

  let {
    inputGroupClassName = "",
    isSerialized,
    serializeFormat,
    split,
  } = _props;

  let onFocusFrom,
    onFocusTo,
    onBlurFrom,
    onBlurTo,
    onCalendarCloseFrom,
    onCalendarCloseTo,
    fromMin,
    fromMax,
    toMin,
    toMax;

  if (Array.isArray(minTime)) {
    [fromMin, toMin] = minTime;
  } else {
    fromMin = toMin = minTime;
  }
  if (Array.isArray(maxTime)) {
    [fromMax, toMax] = maxTime;
  } else {
    fromMax = toMax = maxTime;
  }

  if (Array.isArray(onFocus)) {
    [onFocusFrom, onFocusTo] = onFocus;
  } else {
    onFocusFrom = onFocusTo = onFocus;
  }

  if (Array.isArray(onBlur)) {
    [onBlurFrom, onBlurTo] = onBlur;
  } else {
    onBlurFrom = onBlurTo = onBlur;
  }

  if (Array.isArray(onCalendarClose)) {
    [onCalendarCloseFrom, onCalendarCloseTo] = onCalendarClose;
  } else {
    onCalendarCloseFrom = onCalendarCloseTo = onCalendarClose;
  }

  let _disabled = React.useMemo(() => {
    //TODO need to handle isSerialized
    if (
      !disableForPastDate ||
      selected == null ||
      (isEmptyOrNull(selected.from) && isEmptyOrNull(selected.to))
    ) {
      return disabled;
    }

    let todaysDate = moment().startOf("day");

    let fromValue = selected.from?.dateValue;
    let toValue = selected.to?.dateValue;

    return [
      isEmptyOrNull(fromValue)
        ? false
        : moment(fromValue).isBefore(
            includeTodaysDateForPast
              ? todaysDate.clone().add(1, "days")
              : todaysDate
          ),
      isEmptyOrNull(toValue) ? false : moment(toValue).isBefore(todaysDate),
    ];
  }, [disableForPastDate, includeTodaysDateForPast, selected, disabled]);

  const serializeValue = (selected, key, v) => {
    if (v != null) {
      if (isSerialized)
        v = (customEndDate &&
          moment(v).isBefore(customEndDate) && {
            serialized: moment(customEndDate).format(_props.serializeFormat),
            dateValue: moment(customEndDate, _props.serializeFormat).toDate(),
          }) || {
          serialized: moment(v).format(_props.serializeFormat),
          dateValue: moment(v, _props.serializeFormat).toDate(),
        };
    }

    return { ...selected, [key]: v };
  };

  const onChange = (key, v) => {
    let serializedValue = serializeValue(selected, key, v);

    if (copyTo && key === "from" && v != null && !selected?.to) {
      serializedValue = serializeValue(serializedValue, "to", v);
    }

    if (_props.onChange) _props.onChange(serializedValue);
  };

  const _onCalendarCloseFrom = e => {
    // console.log("_onCalendarCloseFrom", e);
    if (copyTo && !selected?.to && selected?.from && _props.onChange) {
      let serializedValue = serializeValue(
        selected,
        "to",
        isSerialized ? selected.from.dateValue : selected.from
      );

      if (_props.onChange) _props.onChange(serializedValue);
    }

    if (onCalendarCloseFrom) onCalendarCloseFrom(e);
  };

  let _selected = { from: null, to: null, ...selected };

  if (isSerialized && selected != null) {
    if (selected.from) {
      toMin = _selected.from = moment(
        selected.from.serialized,
        serializeFormat
      ).toDate();
    }

    if (selected.to) {
      fromMax = _selected.to = moment(
        selected.to.serialized,
        serializeFormat
      ).toDate();
    }
  }

  if (_selected.from) toMin = _selected.from;
  if (_selected.to) fromMax = _selected.to;

  [fromMin, fromMax, toMin, toMax] = useMemo(() => {
    if (!disablePastDateSelection) {
      return [fromMin, fromMax, toMin, toMax];
    }

    let todaysDate = moment().startOf("day");
    let tomorrowsDate = moment()
      .startOf("day")
      .add(1, "days");

    let fromValue = selected?.from?.dateValue;
    let toValue = selected?.to?.dateValue;
    let _fromMin = tomorrowsDate.toDate();
    let _toMin = toMin;

    if (!isEmptyOrNull(fromValue)) {
      if (moment(fromValue).isBefore(todaysDate.clone().add("1", "days"))) {
        _toMin = todaysDate.toDate();
      }
    } else {
      _toMin = todaysDate
        .clone()
        .add("1", "days")
        .toDate();
    }

    return [_fromMin, fromMax, _toMin, toMax];
  }, [
    fromMin,
    fromMax,
    toMin,
    toMax,
    disablePastDateSelection,
    selected,
    isSerialized,
  ]);

  return (
    <>
      <Row>
        <Col xs="6" {...fromColProps}>
          <div
            className={cx(
              "range-field-wrapper range-from position-relative",
              {
                "floating-label-active":
                  wrapperState.from.isFocused || alwaysFloat[0],
                "field-mandatory": required[0],
              },
              isInvalid[0] && "is-invalid"
            )}
          >
            <Form.Control
              {..._props}
              className={cx(DEFAULT_PROPS.className, _props.className)}
              id={controlId[0]}
              key="from"
              as={DatePicker}
              onFocus={onFocusFrom}
              onBlur={onBlurFrom}
              onCalendarClose={_onCalendarCloseFrom}
              selected={_selected.from}
              onChange={v => {
                onChange("from", v);
              }}
              minDate={fromMin}
              maxDate={fromMax}
              selectsStart
              startDate={_selected.from}
              endDate={_selected.from ? _selected.to : new Date()}
              popperContainer={CalenderContainer}
              disabled={_disabled && _disabled[0]}
              isClearable={
                _disabled && _disabled[0] ? false : _props.isClearable
              }
            />
            {label && (
              <Form.Label htmlFor={controlId[0]}>{label[0]}</Form.Label>
            )}
            <Form.Control.Feedback type="ifx-field-error invalid">
              {wrapperState.from.errorMessage}
            </Form.Control.Feedback>
          </div>
        </Col>
        <Col xs="6" {...toColProps}>
          <div
            className={cx(
              "range-field-wrapper range-to position-relative",
              {
                "floating-label-active":
                  wrapperState.to.isFocused || alwaysFloat[1],
                "field-mandatory": required[1],
              },
              isInvalid[1] && "is-invalid"
            )}
          >
            <Form.Control
              {..._props}
              className={cx(DEFAULT_PROPS.className, _props.className)}
              id={controlId[1]}
              key="to"
              as={DatePicker}
              onFocus={onFocusTo}
              onBlur={onBlurTo}
              onCalendarClose={onCalendarCloseTo}
              selected={_selected.to}
              onChange={v => {
                onChange("to", v);
              }}
              minDate={toMin}
              maxDate={toMax}
              selectsEnd
              startDate={_selected.to ? _selected.from : new Date()}
              endDate={_selected.to}
              popperContainer={CalenderContainer}
              disabled={_disabled && _disabled[1]}
              isClearable={
                _disabled && _disabled[1] ? false : _props.isClearable
              }
            />
            {label && (
              <Form.Label htmlFor={controlId[1]}>{label[1]}</Form.Label>
            )}
            <Form.Control.Feedback type="ifx-field-error invalid">
              {wrapperState.to.errorMessage}
            </Form.Control.Feedback>
          </div>
        </Col>
      </Row>
    </>
  );
};
IFXDateRangePicker.propTypes = {
  isSerialized: PropTypes.bool,
  serializeFormat: PropTypes.string,
};
